import styled from 'styled-components';

// Table Card - wrapper with background matching CustomerTable
export const TableCard = styled.div`
  background: ${(props) =>
    props.theme.card?.background ||
    (props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgb(248, 250, 252)')};
  border: 1px solid
    ${(props) =>
      props.theme.card?.border ||
      (props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 8px;
  box-shadow: ${(props) =>
    props.theme.card?.shadow || 'rgba(0, 0, 0, 0.1) 0px 1px 3px'};
  padding: 32px;
  margin-bottom: 32px;
  transition: box-shadow 0.15s ease;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
    border-radius: 12px;
    max-width: 100%;
    overflow: visible;
  }
`;

// Table Card Header with accent bar
export const TableCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

export const TableCardAccent = styled.div`
  width: 4px;
  height: 24px;
  background: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  border-radius: 2px;
  flex-shrink: 0;
`;

export const TableCardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    props.theme?.name === 'dark'
      ? '#ffffff'
      : props.theme?.colors?.blueBlack || '#000033'};
  margin: 0;
  line-height: 1.4;
`;

// Table Container
export const TableContainer = styled.div`
  overflow-x: auto;
  margin: -32px -32px 0 -32px;
  padding: 32px 32px 0 32px;

  @media (max-width: 768px) {
    margin: 0;
    padding: 0;
    width: 100%;
    display: none;
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(0, 0, 0, 0.2)'
        : 'rgba(0, 0, 0, 0.03)'};
    border-bottom: 1px solid
      ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.1)'};
  }
`;

export const TableHeader = styled.th`
  text-align: left;
  padding: 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#475569')};
  white-space: nowrap;
  text-transform: uppercase;
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'};
  transition: all 0.15s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(0,0,0,0.02)'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const TableCell = styled.td`
  padding: 16px;
  vertical-align: middle;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  font-size: 14px;
`;

export const CellPrimary = styled.div`
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  font-size: 15px;
  margin-bottom: ${(props) => (props.hasSubtext ? '4px' : '0')};
`;

export const CellSecondary = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#94a3b8'};
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

// Mobile Card View - Expandable design
export const MobileCardGrid = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const MobileCard = styled.div`
  background: ${(props) =>
    props.theme.card?.background ||
    (props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff')};
  border: 1px solid
    ${(props) =>
      props.theme.card?.border ||
      (props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
    props.theme.card?.shadow ||
    (props.theme.name === 'dark'
      ? 'rgba(0, 0, 0, 0.2) 0px 1px 3px'
      : 'rgba(0, 0, 0, 0.08) 0px 1px 3px')};
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;

  ${(props) =>
    props.expanded &&
    `
    box-shadow: ${
      props.theme.card?.shadowHover || 'rgba(0, 0, 0, 0.15) 0px 4px 12px'
    };
  `}
`;

export const CardHeader = styled.div`
  padding: 16px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(0,0,0,0.02)'};
  }
`;

export const CardHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CardInfo = styled.div`
  flex: 1;
`;

export const CardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin-bottom: 4px;
`;

export const CardSubtitle = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
`;

export const ExpandIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
  transition: transform 0.3s ease;
  flex-shrink: 0;

  ${(props) =>
    props.expanded &&
    `
    transform: rotate(180deg);
  `}
`;

export const CardDetails = styled.div`
  max-height: ${(props) => (props.expanded ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  border-top: ${(props) =>
    props.expanded
      ? `1px solid ${
          props.theme.name === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.08)'
        }`
      : 'none'};
`;

export const CardDetailsContent = styled.div`
  padding: 16px;
`;

export const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
`;

export const DetailLabel = styled.div`
  font-size: 13px;
  text-transform: uppercase;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
  letter-spacing: 0.05em;
  font-weight: 600;
`;

export const DetailValue = styled.div`
  font-size: 15px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  font-weight: 500;
  text-align: right;
`;

export const MobileActions = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)'};
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.08)'};
`;

// Unified mobile action button - works as both button and link
export const MobileActionButton = styled.button`
  /* Reset for both button and anchor */
  appearance: none;
  text-decoration: none;
  cursor: pointer;

  /* Layout */
  flex: 1;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  /* Typography */
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  font-size: 12px;
  font-weight: 500;

  /* Visual */
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 8px;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#94A3B8')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    transition: color 0.15s ease;
  }

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark' ? '#FA4616' : 'rgba(250,70,22,0.05)'};
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    color: ${(props) =>
      props.theme.name === 'dark'
        ? 'white'
        : props.theme?.colors?.primary || '#FA4616'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(250, 70, 22, 0.25);

    svg {
      color: ${(props) =>
        props.theme.name === 'dark'
          ? 'white'
          : props.theme?.colors?.primary || '#FA4616'};
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// Empty State
export const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

// Loading State
export const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
`;

// Pagination
export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;

  @media (max-width: 768px) {
    margin-top: 16px;
  }
`;

export const PageEllipsis = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
  margin: 0 8px;
  font-size: 14px;
`;

// Section Header for table titles
export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
`;

export const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
`;

// Mobile Quick Info - compact grid display for key metrics
export const MobileQuickInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 12px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
`;

export const MobileQuickInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;

  ${DetailLabel} {
    font-size: 11px;
  }

  ${DetailValue} {
    font-size: 14px;
    text-align: center;
  }
`;
